import {
	FundProductInterface,
	BarrierType,
	CouponFrequency,
	FundProductStatusesEnum,
	FundProductTypesEnum,
	ICouponPayment,
	IEarlyRedemption,
	IUnderlyingInstrument,
	ProductCategory,
	ProductGroup,
	ProductSubType,
	SettlementType,
	WarrantType,
	FundOrderTypesEnum,
	IProductDocument, IProductProperties, FundProductPriceInterface, ProductFieldNames,
} from '@spa/api';
import {RadioGroupControl} from 'src/app/shared/classes';
import {IPaginationConfig} from 'src/app/shared/components';

export interface IProductTableColumns {
	name?: boolean;
	version?: boolean;
	subType?: boolean;
	type?: boolean;
	underlyings?: boolean;
	ccy: boolean;
	expiresOn?: boolean;
	durationInMonths?: boolean;
	subscriptionPeriodEndsOn?: boolean;
	annualInterestRate?: boolean;
	barrier?: boolean;
	strikeLevel?: boolean;
	memoryEffect?: boolean;
	capLevel?: boolean;
	bonusLevel?: boolean;
	warrantType?: boolean;
	conversionRatio?: boolean;
	capitalProtection?: boolean;
	participation?: boolean;
	annualizedYieldToMaturity?: boolean;
	yearToDateYield?: boolean;
	trailingTwelveMonthsYield?: boolean;
	netAssetValue?: boolean;
}

export interface IGroupedProductsItem {
	products: Product[];
	pagedProducts: Product[];
	issuedBy?: string;
	pagination: IPaginationConfig;
}

export interface IGroupedProducts {
	subType?: ProductSubType;
	items: IGroupedProductsItem[];
}

export class ProductFilter {
	type?: FundProductTypesEnum[];
	status?: FundProductStatusesEnum[];
	issuedBy?: string[];
	category?: ProductCategory[];
	group?: ProductGroup[];
	subType?: ProductSubType[];
	annualInterestRate?: number[];
	expiresOn?: number[];
	capitalProtection?: number[];
	barrier?: number[];
	keyword?: string;
	annualizedYieldToMaturity?: number[];

	reset(): void {
		this.keyword = null;
		this.type = null;
		this.status = null;
		this.issuedBy = null;
		this.category = null;
		this.group = null;
		this.subType = null;
		this.annualInterestRate = null;
		this.expiresOn = null;
		this.capitalProtection = null;
		this.annualizedYieldToMaturity = null;

		this.set(ProductFieldNames.Type, [FundProductTypesEnum.Bonds]);
		this.set(ProductFieldNames.SubType, [ProductSubType.GovernmentBonds]);
	}

	setKeyword(keyword: string): void {
		this.keyword = keyword?.length > 1 ? keyword : null;
	}

	set(name: string, values: (string | number)[]): void {
		const value = values?.length > 0 ? values : null;

		Object.assign(this, {
			[name]: value
		});
	}

	get hasValue(): boolean {
		const hasValue = (values: any[] | string): boolean => {
			return values && values.length > 0;
		};

		return hasValue(this.annualizedYieldToMaturity)
			|| hasValue(this.keyword)
			|| hasValue(this.status)
			|| hasValue(this.issuedBy)
			|| hasValue(this.category)
			|| hasValue(this.group)
			|| hasValue(this.expiresOn);
	}
}

export interface IProductControls {
	navigation: { options: { label: string, value: { type: number, subType: number } }[] };
	type: RadioGroupControl;
	status: RadioGroupControl;
	issuedBy: RadioGroupControl;
	category: RadioGroupControl;
	group: RadioGroupControl;
	subType: RadioGroupControl;
	annualInterestRate: RadioGroupControl;
	expiresOn: RadioGroupControl;
	capitalProtection: RadioGroupControl;
	barrier: RadioGroupControl;
	annualizedYieldToMaturity: RadioGroupControl;
}

export class UnderlyingInstrument implements IUnderlyingInstrument {
	constructor(item: IUnderlyingInstrument) {
		this.name = item.name;
		this.symbol = item.symbol;
		this.currency = item.currency;
		this.price = item.price;
		this.weight = item.weight;
		this.initialFixingLevel = item.initialFixingLevel;
	}

	name: string;
	symbol: string;
	currency: string;
	price?: number;
	weight?: number;
	initialFixingLevel?: number;

	get initialFixingValue(): number {
		return this.initialFixingLevel ? (this.initialFixingLevel * this.price) : 0;
	}
}

export type ProductDialogType = {
	product: Product,
	orderType?: FundOrderTypesEnum,
	isPreview?: boolean
};

export class Product {
	constructor(product: FundProductInterface) {
		Object.keys(product).forEach(key => {
			// @ts-ignore
			this[key] = product[key];
		});
	}

	hidden?: boolean;
	id: number;
	tenantId: number;
	status: FundProductStatusesEnum;
	productId: string;
	code: string;
	type: FundProductTypesEnum;
	buyPrice: FundProductPriceInterface;
	sellPrice: FundProductPriceInterface;
	subType: ProductSubType;
	group: ProductGroup;
	category: ProductCategory;
	name: string;
	nameBG: string;
	version: string;
	isin: string;
	issuedBy: string;
	guarantor: string;
	listingMarket: string;
	exchangeSymbol: string;
	currency: string;
	crossCurrency: boolean;
	nominalValue: number;
	issuePrice: number;
	openEnd: boolean;
	durationInMonths: number;
	settlementType: SettlementType;
	subscriptionPeriodStartsOn?: Date;
	subscriptionPeriodEndsOn?: Date;
	initialFixingDate?: Date;
	firstExchangeTradingDate?: Date;
	issuedOn?: Date;
	finalFixingDate?: Date;
	lastExchangeTradingDate?: Date;
	expiresOn?: Date;
	redemption?: Date;
	conditionalCoupon: boolean;
	memoryEffect: boolean;
	annualInterestRate: number;
	interestPaymentPeriod: CouponFrequency;
	couponTriggerLevel?: number;
	participation: number;
	capLevel: number;
	short: boolean;
	capitalProtection?: number;
	barrier: number;
	barrierType: BarrierType;
	worstPerformer: boolean;
	strikeLevel: number;
	bonusLevel: number;
	bonusAmount: number;
	warrantType: WarrantType;
	conversionRatio: number;
	initialLeverage: number;
	buyOrdersAllowed: boolean;
	sellOrdersAllowed: boolean;
	minimumBuyOrderSize?: number;
	maximumBuyOrderSize?: number;
	withdrawalBeforeMaturityFeeMin: number;
	withdrawalBeforeMaturityFeeMax: number;
	pictureFileStorageId: string;
	createdBy: string;
	approvedBy: string;
	lastUpdatedBy: string;
	createdOn: Date;
	approvedOn?: Date;
	lastUpdatedOn?: Date;
	couponPayments: ICouponPayment[];
	earlyRedemptions: IEarlyRedemption[];
	underlyingInstruments: IUnderlyingInstrument[];
	productDocuments: IProductDocument[];
	properties: IProductProperties;

	get annualizedYieldToMaturity(): number | null {
		return this.properties?.annualizedYieldToMaturity;
	}

	get yearToDateYield(): number | null {
		return this.properties?.yearToDateYield;
	}

	get trailingTwelveMonthsYield(): number | null {
		return this.properties?.trailingTwelveMonthsYield;
	}

	get publicOfferingYield(): number | null {
		return this.properties?.publicOfferingYield;
	}

	get netAssetValue(): number | null {
		return this.properties?.netAssetValue;
	}

	get penalRedemptionPrice(): number | null {
		return this.properties?.penalRedemptionPrice;
	}

	get earlyRedemptionAmount(): number | null {

		if (!this.earlyRedemptions || this.earlyRedemptions.length === 0) {
			return null;
		}

		if (this.earlyRedemptions.length === 1) {
			return this.earlyRedemptions[0].amount;
		}

		const allAmounts = this.earlyRedemptions.map(item => item.amount).filter(this.onlyUnique);
		if (allAmounts.length === 1) {
			return allAmounts[0];
		}

		return null;
	}

	get couponPaymentNextObservationDate(): Date | null {
		if (!this.couponDatesShown()) {
			return null;
		}

		return this.getNextDate(this.couponPayments.map(item => item.observationDate));
	}

	get couponPaymentNextPaymentDate(): Date | null {
		if (!this.couponDatesShown()) {
			return null;
		}

		return this.getNextDate(this.couponPayments.map(item => item.paymentDate));
	}

	get earlyRedemptionLastDate(): Date | null {
		if (!this.earlyRedemptionDatesShown()) {
			return null;
		}

		return this.getLastDate(this.earlyRedemptions.map(item => item.observationDate));
	}

	get earlyRedemptionNextDate(): Date | null {
		if (!this.earlyRedemptionDatesShown()) {
			return null;
		}

		return this.getNextDate(this.earlyRedemptions.map(item => item.observationDate));
	}

	private getLastDate(dates: Date[]): Date | null {
		const allDates = dates
			.map(item => new Date(item))
			.filter(this.onlyUnique)
			.sort((date1, date2) => date1.getTime() - date2.getTime());

		return allDates[allDates.length - 1];
	}

	private getNextDate(dates: Date[]): Date | null {
		const todayDate = new Date();

		const allDates = dates
			.map(item => new Date(item))
			.filter(this.onlyUnique)
			.sort((date1, date2) => date1.getTime() - date2.getTime())
			.filter(date => {
				return date > todayDate;
			});

		return allDates.length > 0 ? allDates[0] : null;
	}

	private earlyRedemptionDatesShown(): boolean {
		if (!this.earlyRedemptions || this.earlyRedemptions.length <= 1) {
			return false;
		}

		const allDates = this.earlyRedemptions.map(item => item.observationDate).filter(this.onlyUnique);
		if (allDates.length === 1) {
			return false;
		}

		return true;
	}

	private couponDatesShown(): boolean {
		if (!this.couponPayments || this.couponPayments.length <= 1) {
			return false;
		}

		return true;
	}

	private onlyUnique(value: any, index: any, self: any): boolean {
		if (!value) {
			return false;
		}
		return self.indexOf(value) === index;
	}

}
